import React from "react";
import { useNavigate } from "react-router-dom";
import { css, cx } from "@emotion/css";
import {
  IoLocationOutline,
  IoCalendarOutline,
  IoTicketOutline,
  IoTimeOutline,
} from "react-icons/io5";
import { Button } from "../../../../shared/ui/button/button";
import { ShareButton } from "../../../../shared/components/share-button/share-button";
import type { Event } from "../../../../api/services/events";

interface EventCardProps extends React.HTMLAttributes<HTMLDivElement> {
  event: Event;
}

const card = css({
  position: "relative",
  borderRadius: "var(--spacing-md)",
  border: "1px solid rgba(var(--pcrgba-200), 0.5)",
  backgroundColor: "var(--primary-color)",
  boxShadow: "var(--shadow-200)",
  cursor: "pointer",
  ":hover": {
    boxShadow: "var(--shadow-400)",
    borderColor: "rgba(var(--pcrgba-400), 0.7)",
  },
});

const imageWrapper = css({
  width: "100%",
  height: "200px",
  position: "relative",
  "& img": {
    width: "100%",
    height: "100%",
    objectFit: "cover",
    objectPosition: "center",
    borderTopLeftRadius: "var(--spacing-md)",
    borderTopRightRadius: "var(--spacing-md)",
  },
  "@media (max-width: 920px)": {
    height: "160px",
  },
});

const imagePlaceholder = css({
  width: "100%",
  height: "100%",
  borderTopLeftRadius: "var(--spacing-md)",
  borderTopRightRadius: "var(--spacing-md)",
  backgroundColor: "rgba(var(--pcrgba-300), 0.25)",
});

const statusBadge = css({
  position: "absolute",
  top: "var(--spacing-xs)",
  right: "var(--spacing-xs)",
  borderRadius: "20px",
  padding: "var(--spacing-4xs) var(--spacing-2xs)",
  display: "flex",
  alignItems: "center",
  gap: "var(--spacing-4xs)",
  fontSize: "var(--fs-300)",
  fontWeight: 600,
  backdropFilter: "blur(5px)",
  boxShadow: "var(--shadow-200)",
  zIndex: 5,
  transition: "box-shadow 0.2s ease",
  "@media (max-width: 920px)": {
    fontSize: "var(--fs-250)",
  },
  "@media (max-width: 480px)": {
    "& span": {
      display: "none",
    },
  },
});

const shareButtonWrapper = css({
  position: "absolute",
  top: "var(--spacing-xs)",
  left: "var(--spacing-xs)",
  zIndex: 10,
});

const statusAvailable = css({
  backgroundColor: "rgba(var(--pcrgba-600), 0.9)",
  color: "var(--primary-color)",
  border: "1px solid rgba(var(--pcrgba-700), 0.5)",
});

const statusWaiting = css({
  backgroundColor: "rgba(var(--pcrgba-100), 0.9)",
  color: "var(--pc-600)",
  border: "1px solid rgba(var(--pcrgba-300), 0.5)",
});

const info = css({
  display: "flex",
  flexDirection: "column",
  padding: "var(--spacing-xs) var(--spacing-2xs)",
});

const titleWrapper = css({
  fontSize: "var(--fs-400)",
  fontWeight: 600,
  height: "calc(var(--fs-400) * 2.5)",
  marginBottom: "var(--spacing-2xs)",
  color: "var(--pc-700)",
  overflow: "hidden",
  display: "-webkit-box",
  WebkitLineClamp: 2,
  WebkitBoxOrient: "vertical",
  "@media (max-width: 920px)": {
    fontSize: "var(--fs-350)",
    height: "calc(var(--fs-350) * 2.5)",
    marginBottom: "var(--spacing-3xs)",
  },
});

const extraInfo = css({
  display: "flex",
  flexDirection: "column",
  gap: "var(--spacing-4xs)",
  fontSize: "var(--fs-350)",
  paddingBottom: "var(--spacing-xs)",
  color: "rgba(var(--pcrgba-500), 0.8)",
  "@media (max-width: 920px)": {
    fontSize: "var(--fs-300)",
  },
});

const infoItem = css({
  display: "flex",
  alignItems: "center",
  gap: "var(--spacing-4xs)",
  "& svg": {
    flexShrink: 0,
  },
});

const actionButton = css({
  fontSize: "var(--fs-350)",
  padding: "var(--spacing-2xs)",
  "@media (max-width: 920px)": {
    fontSize: "var(--fs-300)",
    padding: "var(--spacing-3xs)",
  },
});

export const EventCard: React.FC<EventCardProps> = ({
  event,
  className,
  ...rest
}) => {
  const navigate = useNavigate();

  const getEventImageUrl = () => {
    // Some APIs return name+extension instead of a direct URL
    const poster = event?.posterImage as unknown as {
      name?: string;
      extension?: string; // may come with or without leading dot
      url?: string;
    };

    const serverUrl = import.meta.env.VITE_API_BASE_URL || "";

    if (poster?.name && poster?.extension) {
      const ext = poster.extension.startsWith('.') ? poster.extension : `.${poster.extension}`;
      return `${serverUrl}/assets/${poster.name}${ext}`;
    }

    if (poster?.url) {
      return poster.url;
    }

    return undefined;
  };

  const hasSchedules = event?.schedules && event.schedules.length > 0;

  const formatDate = () => {
    if (!hasSchedules) return "Datas a definir";
    const earliestDate = event.schedules[0].date;
    // Normalize to UTC date-only to avoid timezone shifts
    const ymd = (earliestDate.match(/^\d{4}-\d{2}-\d{2}/)?.[0] ?? earliestDate.split('T')[0] ?? earliestDate);
    const date = new Date(`${ymd}T00:00:00Z`);
    return date.toLocaleDateString("pt-BR", {
      day: "numeric",
      month: "short",
      year: "numeric",
      timeZone: "UTC",
    });
  };

  const formatPrice = () => {
    return "Consulte valores";
  };

  const handleCardClick = (e: React.MouseEvent) => {
    if ((e.target as HTMLElement).closest("button")) {
      return;
    }
    navigate(`/evento/${event.id}`);
  };

  const handleButtonClick = () => {
    navigate(`/evento/${event.id}`);
  };

  return (
    <div className={cx(card, className)} onClick={handleCardClick} {...rest}>
      <div className={imageWrapper}>
        {(() => {
          const src = getEventImageUrl();
          return src ? (
            <img src={src} alt={event.title} loading="lazy" />
          ) : (
            <div className={imagePlaceholder} aria-label="Sem imagem" />
          );
        })()}
        <div
          className={cx(
            statusBadge,
            hasSchedules ? statusAvailable : statusWaiting
          )}
        >
          <IoTimeOutline size={14} />
          <span>{hasSchedules ? "Disponível" : "Em espera"}</span>
        </div>
        <div className={shareButtonWrapper}>
          <ShareButton
            url={`${window.location.origin}/evento/${event.id}`}
            title={event.title}
          />
        </div>
      </div>

      <div className={info}>
        <div className={titleWrapper}>{event.title}</div>

        <div className={extraInfo}>
          <div className={infoItem}>
            <IoLocationOutline size={16} />
            <span>
              {hasSchedules ? event.schedules[0].location.name : "A definir"}
            </span>
          </div>
          <div className={infoItem}>
            <IoCalendarOutline size={16} />
            <span>{formatDate()}</span>
          </div>
          <div className={infoItem}>
            <IoTicketOutline size={16} />
            <span>{formatPrice()}</span>
          </div>
        </div>

        <Button className={actionButton} onClick={handleButtonClick}>
          {hasSchedules ? "Comprar ingressos" : "Lista de espera"}
        </Button>
      </div>
    </div>
  );
};

export default EventCard;
